import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  FormControl,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DialogActions from '@mui/material/DialogActions';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { getBanks } from 'src/utils/accounts/banks';
import { useLocales } from 'src/locales';
import { useDispatch } from 'react-redux';
import { addRibToCompany } from 'src/redux/slices/dataSlice';

interface RibDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
}

interface Bank {
  id: number;
  name: string;
}

const RibDialog: React.FC<RibDialogProps> = ({ open, onClose, companyId, companyName }) => {
  const banks: Bank[] = getBanks();
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [inputType, setInputType] = useState<string>('');
  const [rib, setRib] = useState<string>('');
  const [bref, setBref] = useState<string>('');
  const [agence, setAgence] = useState<string>('');
  const dispatch = useDispatch();
  const { t } = useLocales();
  const { makeRequest } = useServerRequest();

  const handleRibChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\s+/g, '');
    if (/^\d*$/.test(input) && input.length <= 24) {
      const formattedRib = input.replace(/(.{4})/g, '$1 ').trim();
      setRib(formattedRib);
    }
  };

  const handleSubmit = async () => {
    if (!selectedBank) return;

    const formattedRib = rib.replace(/\s+/g, '');
    const formData = {
      company_id: companyId,
      bank_id: selectedBank.id,
      rib: formattedRib,
      bref,
      agence,
    };

    const response = await makeRequest(
      `/saveRib`,
      {
        method: 'POST',
        data: formData,
      },
      t('common.alert.rib')
    );

    if (response.success && response.data) {
      dispatch(
        addRibToCompany({
          companyId,
          rib: response.data.rib,
        })
      );
      onClose();
    }
  };

  return (
    <Dialog open={open} disableEscapeKeyDown onClose={(_, reason) => reason !== 'backdropClick' && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('m.t.rib', { name: companyName })}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <Autocomplete
            fullWidth
            value={selectedBank}
            options={banks}
            getOptionLabel={(option) => option.name}
            onChange={(event, newValue) => setSelectedBank(newValue)}
            inputValue={inputType}
            onInputChange={(event, newInputValue) => setInputType(newInputValue)}
            renderInput={(params) => <TextField {...params} label="Select Bank" />}
          />
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label={t('common.input.rib.label')}
          value={rib}
          onChange={handleRibChange}
          placeholder={t('common.input.rib.select')}
          helperText={t('common.input.rib.error1')}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('common.input.abr.label')}
          value={bref}
          onChange={(e) => setBref(e.target.value.toUpperCase())}
          placeholder="ABC1234"
        />

        <TextField
          fullWidth
          margin="normal"
          label={t('common.input.agence.label')}
          value={agence}
          onChange={(e) => setAgence(e.target.value.toUpperCase())}
          placeholder="Casablanca, Agadir,.."
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('common.button.action.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!selectedBank || !rib}>
          {t('common.button.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RibDialog;
